import productService from '@src/quince-site-common/src/services/productService'
import type { ISvdCard } from '@src/types/svd-product-card.types'
import { getStorage } from '@src/utils/enhancedStorage'

import wishListStateManager from '../../redux/globalStateManager/WishlistStateManager'
import { IWishlistItem, WishListItem } from '../Wishlist.types'
import { WISHLIST_LOCAL_STORAGE_KEY } from '../wishlistService'

/**
 * SVD client-side wishlist store (Phase 2). Reads/writes the SAME `wishlist` localStorage key as the
 * legacy service, but works in the SVD identity (`productId + selectedAttributes`) and writes the
 * superset entry (`{ product_id: spid, color, productId, selectedAttributes }`) so legacy readers
 * still see spid+color. Only invoked under the SVD gate, so non-SVD sessions never touch it.
 */

export interface SvdWishlistEntry {
  /** Product-level Shopify spid — kept for legacy-card compat + the guest bridge. */
  spid: string
  /** Selected anchor value (colour) — legacy compat. */
  color: string
  /** Internal SAND productId — the SVD identity. */
  productId: string
  selectedAttributes: Record<string, string>
}

function readRaw(): WishListItem[] {
  const raw = getStorage().getItem<string>(WISHLIST_LOCAL_STORAGE_KEY, { isGlobal: false })
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as IWishlistItem[]
    return parsed.map(item => new WishListItem(item))
  } catch {
    return []
  }
}

function writeRaw(items: WishListItem[]): void {
  getStorage().setItem(WISHLIST_LOCAL_STORAGE_KEY, JSON.stringify(items), { isGlobal: false })
  wishListStateManager.setWishlistTotalItemsCount(items.length)
}

/** True if the (productId, selectedAttributes) pair is already wishlisted in the local store. */
export function isSvdItemInWishlist(productId: string, selectedAttributes: Record<string, string>): boolean {
  const target = WishListItem.getSvdWishListId({ productId, selectedAttributes })
  return readRaw().some(item => item.productId && WishListItem.getSvdWishListId(item) === target)
}

export function addSvdItemToLocalStore(entry: SvdWishlistEntry): void {
  const items = readRaw()
  const svdId = WishListItem.getSvdWishListId(entry)
  if (items.some(item => item.productId && WishListItem.getSvdWishListId(item) === svdId)) return
  items.push(
    new WishListItem({
      product_id: entry.spid,
      color: entry.color,
      productId: entry.productId,
      selectedAttributes: entry.selectedAttributes,
    }),
  )
  writeRaw(items)
}

export function removeSvdItemFromLocalStore(productId: string, selectedAttributes: Record<string, string>): void {
  const svdId = WishListItem.getSvdWishListId({ productId, selectedAttributes })
  const items = readRaw().filter(item => !(item.productId && WishListItem.getSvdWishListId(item) === svdId))
  writeRaw(items)
}

/**
 * Lazy, non-destructive backfill: for legacy spid+color entries missing a `productId`, resolve
 * spid→productId (cached) and rewrite to the superset. Never drops an entry on a failed resolve
 * (retried next visit). Idempotent — entries already carrying productId are skipped. SVD-gated.
 */
export async function backfillSvdWishlist(): Promise<void> {
  const items = readRaw()
  const legacyOnly = items.filter(item => !item.productId && item.product_id)
  if (legacyOnly.length === 0) return

  const spidToProductId = await productService.resolveSpidsToProductIds(legacyOnly.map(item => String(item.product_id)))
  if (spidToProductId.size === 0) return

  let changed = false
  const upgraded = items.map(item => {
    if (item.productId) return item
    const productId = spidToProductId.get(String(item.product_id))
    if (!productId) return item // unresolved (e.g. retired) — keep as-is, retry next visit
    changed = true
    return new WishListItem({
      product_id: item.product_id,
      color: item.color,
      productId,
      selectedAttributes: { color: item.color },
    })
  })
  if (changed) writeRaw(upgraded)
}

/**
 * Local superset entries that carry a productId and are NOT already confirmed by the server — the
 * ones still needing a push to `/v2`. Entries `syncSvdWishlistFromCards` last wrote from an
 * authoritative server read are marked `svdSynced` and excluded here, so a repeat login-merge (it
 * also runs on every wishlist-page view, as a safety net for the fire-and-forget login-event merge)
 * doesn't re-push the same already-saved items to `bulk-update-user-wishlist` on every visit.
 */
export function readSvdSupersetEntries(): SvdWishlistEntry[] {
  return readRaw()
    .filter(item => item.productId && !item.svdSynced)
    .map(item => ({
      spid: String(item.product_id),
      color: item.color,
      productId: item.productId as string,
      selectedAttributes: item.selectedAttributes ?? (item.color ? { color: item.color } : {}),
    }))
}

/**
 * Overwrite the local cache from the authoritative `/v2` cards (login-merge sync), while PRESERVING
 * any local entries that lack a productId (unresolved legacy — never dropped). Each server card's
 * wishlisted variant is `cardVariants[0]` (v2 returns it first + echoes its selectedAttributes).
 *
 * `/v2/get-active` is treated as authoritative for resolved (productId-bearing) entries: after the
 * login-merge push, the server IS the source of truth, so its list replaces the resolved locals. The
 * client does NOT paper over a backend read that comes back short — that's a BE correctness concern,
 * fixed on the BE. Only unresolved legacy entries (never pushed, so the server can't know them) are
 * carried forward.
 */
export function syncSvdWishlistFromCards(cards: ISvdCard[]): void {
  const serverEntries = cards.map(card => {
    const variant = card.cardVariants[0]
    const color = variant?.displayConfig?.value ?? ''
    return new WishListItem({
      product_id: String(card.relatedIds.spid),
      color,
      productId: String(card.productId),
      selectedAttributes: variant?.selectedAttributes ?? (color ? { color } : {}),
      svdSynced: true,
    })
  })
  const unresolvedLocal = readRaw().filter(item => !item.productId)
  writeRaw([...unresolvedLocal, ...serverEntries])
}
